// Types for the Data Ingestion & Ontology feature.
// Shapes match the migration columns 1:1 (ontology_tables migration).

// ── Property kind ─────────────────────────────────────────────────────────────

export type PropertyKind = "string" | "number" | "date" | "bool" | "currency" | "enum" | "ref";

// ── Sniffed CSV column type (subset of PropertyKind) ─────────────────────────

export type SniffedType = "string" | "number" | "date" | "bool";

// ── Schema inference result (stored in data_sources.schema) ──────────────────

export interface ColumnSchema {
  name: string;
  sniffed_type: SniffedType;
  null_rate: number; // 0–1 fraction of empty cells
  uniqueness: number; // 0–1 fraction of distinct values
  samples: string[]; // first 5 distinct non-empty values, ≤ 200 chars each
}

export interface DataSourceSchema {
  columns: ColumnSchema[];
  header_signature: string; // sha256:... of sorted column names
}

// ── Data sources ─────────────────────────────────────────────────────────────

export interface DataSource {
  id: string;
  kind: "csv";
  name: string;
  storage_path: string | null;
  schema: DataSourceSchema;
  row_count: number | null;
  uploaded_by: string | null;
  uploaded_at: string;
  deleted_at: string | null;
}

// ── Property types ────────────────────────────────────────────────────────────

export interface PropertyType {
  id: string;
  object_type_id: string;
  name: string;
  api_name: string;
  kind: PropertyKind;
  required: boolean;
  unique_in_type: boolean;
  enum_values: string[] | null;
  ref_object_type_id: string | null;
  currency_code: string | null;
  description: string | null;
  position: number;
  created_at: string;
}

// ── Object types ──────────────────────────────────────────────────────────────

export interface ObjectType {
  id: string;
  name: string;
  api_name: string;
  description: string | null;
  icon: string | null;
  display_property: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Object type with its property types eager-loaded. */
export interface ObjectTypeWithProperties extends ObjectType {
  property_types: PropertyType[];
}

// Input shape for save_object_type RPC p_input parameter.
export interface SaveObjectTypeInput {
  name: string;
  api_name?: string; // only used on create; immutable after first save
  description?: string | null;
  icon?: string | null;
  display_property?: string | null;
  property_types: SavePropertyTypeInput[];
}

export interface SavePropertyTypeInput {
  id?: string; // omit for new properties
  name: string;
  api_name: string;
  kind: PropertyKind;
  required?: boolean;
  unique_in_type?: boolean;
  enum_values?: string[] | null;
  ref_object_type_id?: string | null;
  currency_code?: string | null;
  description?: string | null;
  position?: number;
}

// ── Mapping templates ─────────────────────────────────────────────────────────

export interface Mapping {
  id: string;
  object_type_id: string;
  name: string;
  primary_key_column: string;
  column_map: Record<string, string>; // { property_api_name: source_column_name }
  header_signature: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// ── Materializations ──────────────────────────────────────────────────────────

export type MaterializationStatus = "pending" | "running" | "succeeded" | "failed";

export interface Materialization {
  id: string;
  object_type_id: string;
  mapping_id: string;
  data_source_id: string;
  status: MaterializationStatus;
  rows_inserted: number;
  rows_failed: number;
  error: string | null;
  started_by: string | null;
  started_at: string;
  finished_at: string | null;
}

// ── Object instances ──────────────────────────────────────────────────────────

export interface ObjectInstance {
  id: string;
  object_type_id: string;
  materialization_id: string;
  pk_value: string;
  data: Record<string, unknown>;
  source_row_index: number;
  searchable_text: string;
  created_at: string;
  transform_run_id: string | null;
}

// ── Transforms ────────────────────────────────────────────────────────────────

export type TransformTriggerMode = "manual" | "on_upstream_materialize";
export type TransformRunStatus = "pending" | "running" | "succeeded" | "failed";
export type TransformRunTrigger = "manual" | "on_upstream_materialize";

export interface Transform {
  id: string;
  name: string;
  api_name: string;
  description: string | null;
  source_object_type_id: string;
  target_object_type_id: string;
  code: string;
  trigger_mode: TransformTriggerMode;
  current_version: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface TransformVersion {
  id: string;
  transform_id: string;
  version_number: number;
  code: string;
  description: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
}

export interface TransformRun {
  id: string;
  transform_id: string;
  transform_version: number;
  status: TransformRunStatus;
  trigger: TransformRunTrigger;
  upstream_materialization_id: string | null;
  rows_in: number;
  rows_out: number;
  rows_failed: number;
  error: string | null;
  started_by: string | null;
  started_at: string;
  finished_at: string | null;
}

/** Input shape for save_transform RPC p_input parameter. */
export interface SaveTransformInput {
  name: string;
  api_name?: string; // only used on create; immutable after first save
  description?: string | null;
  source_object_type_id: string;
  target_object_type_id: string;
  code: string;
  trigger_mode: TransformTriggerMode;
}

// ── Transform targets (multi-target) ─────────────────────────────────────────

/** A row in the transform_targets join table (one per additional target Object Type). */
export interface TransformTarget {
  id: string;
  transform_id: string;
  object_type_id: string;
  position: number;
  created_at: string;
}

/**
 * Shape of an output row from a multi-target transform.
 * `object_type` is the api_name of the target Object Type this row belongs to.
 * When omitted, run-transform falls back to target_object_type_id (single-target compat).
 */
export interface MultiTargetOutputRow {
  object_type?: string; // api_name of target Object Type; optional for back-compat
  pk: string;
  data: Record<string, unknown>;
}

// ── CSV scaffold (used by TransformEditorSheet Part 1) ────────────────────────

/** Parsed CSV structure used to guide transform authoring. */
export interface CsvGuide {
  headers: string[];
  /** First few sample rows (up to 3) for the preview panel. */
  sampleRows: Record<string, string>[];
}

// ── Errors ────────────────────────────────────────────────────────────────────

/** Raised by useSaveObjectType when the DB returns sqlstate P0001 (stale_version). */
export class StaleVersionError extends Error {
  constructor(message = "stale_version") {
    super(message);
    this.name = "StaleVersionError";
  }
}

/** Raised by useSoftDeleteObjectType when the DB returns sqlstate P0004 (active mappings). */
export class ActiveMappingsError extends Error {
  constructor(message = "cannot delete object type with active mappings") {
    super(message);
    this.name = "ActiveMappingsError";
  }
}

/**
 * Raised by useSaveObjectType when the DB returns sqlstate 22023 (invalid_parameter_value)
 * because api_name violates the naming convention (^[a-z][a-z0-9_]*$).
 */
export class InvalidApiNameError extends Error {
  constructor(message = "api_name violates naming convention") {
    super(message);
    this.name = "InvalidApiNameError";
  }
}

/**
 * Raised by useSaveObjectType when the DB returns sqlstate P0005 because enabling
 * `required` or `unique_in_type` on a property would be violated by existing
 * materialized objects. The DB message is machine-parseable and prefixed with
 * `required_violation:` or `unique_violation:`.
 */
export class DataConstraintViolationError extends Error {
  constructor(message = "existing data would violate the new constraint") {
    super(message);
    this.name = "DataConstraintViolationError";
  }
}
